import Swal from 'sweetalert2';

const parseArray = (arr) => {
  if (!arr) return [];
  if (Array.isArray(arr)) return arr;
  if (typeof arr === 'string') {
    try {
      const parsed = JSON.parse(arr);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }
  return [];
};

const hasAnyDataInRows = (rows) => {
  if (!Array.isArray(rows) || rows.length === 0) return false;
  return rows.some(row => {
    return Object.values(row || {}).some(v => v !== '' && v !== null && v !== undefined);
  });
};

export const validarCedulaRepresentanteEscombros = (acta, caso) => {
  try {
    const tipoCaso = caso?.tipo_caso;
    if (tipoCaso === 'ESCOMBROS' || tipoCaso === 'ESPECIALES') {
      if (acta?.sin_firma || acta?.cancelado) return true;
      const cedula = (acta?.representante_cedula || '').toString().trim();
      if (!cedula) {
        Swal.fire({
          icon: 'warning',
          title: 'Validación',
          text: 'En casos de tipo "ESCOMBROS", la cédula del representante es obligatoria.',
          confirmButtonColor: '#dc3545'
        });
        return false;
      }
    }
    return true;
  } catch (e) {
    return true;
  }
};

export const validarUNRCompleto = (acta) => {
  try {
    let unr = acta?.unidades_no_residenciales_detalle;
    if (!unr) return true;
    if (typeof unr === 'string') {
      try { unr = JSON.parse(unr || '[]'); } catch (_) { unr = []; }
    }
    if (!Array.isArray(unr) || unr.length === 0) return true;

    const requiredFields = ['piso','actividad_nombre','largo','ancho','ocupadas','vacias'];
    const isFilled = (v) => v !== '' && v !== null && v !== undefined;

    for (let i = 0; i < unr.length; i++) {
      const row = unr[i] || {};
      const anyValue = requiredFields.some((k) => isFilled(row[k]));
      if (anyValue) {
        const missing = requiredFields.filter((k) => !isFilled(row[k]));
        if (missing.length > 0) {
          Swal.fire({
            icon: 'error',
            title: 'No se puede finalizar',
            text: `En Unidades No Residenciales, fila ${i + 1}: complete todos los campos obligatorios antes de finalizar.`,
            confirmButtonColor: '#dc3545'
          });
          return false;
        }
      }
    }
    return true;
  } catch (e) {
    return true;
  }
};

export const validarURCompleto = (acta) => {
  try {
    let ur = acta?.unidades_residenciales_detalle;
    if (!ur) return true;
    if (typeof ur === 'string') {
      try { ur = JSON.parse(ur || '[]'); } catch (_) { ur = []; }
    }
    if (!Array.isArray(ur) || ur.length === 0) return true;

    const requiredFields = ['piso','ur','ocupadas','vacias'];
    const isFilled = (v) => v !== '' && v !== null && v !== undefined;

    for (let i = 0; i < ur.length; i++) {
      const row = ur[i] || {};
      const anyValue = requiredFields.some((k) => isFilled(row[k]));
      if (anyValue) {
        const missing = requiredFields.filter((k) => !isFilled(row[k]));
        if (missing.length > 0) {
          Swal.fire({
            icon: 'error',
            title: 'No se puede finalizar',
            text: `En Unidades Residenciales, fila ${i + 1}: complete todos los campos obligatorios (Piso, UR, Ocupadas, Vacías) antes de finalizar.`,
            confirmButtonColor: '#dc3545'
          });
          return false;
        }
      }
    }
    return true;
  } catch (e) {
    return true;
  }
};

export const validarCamposCondicionales = (acta) => {
  try {
    const errores = [];
    const isFilled = (v) => v !== '' && v !== null && v !== undefined;

    if (acta?.cuenta_suscriptor_activo && !isFilled(acta?.suscriptor_activo_detalles)) {
      errores.push('El campo "Detalles del Suscriptor Activo" es obligatorio cuando "Cuenta suscriptor activo" está marcado.');
    }
    if (acta?.demolido && !isFilled(acta?.causa_demolicion)) {
      errores.push('El campo "Causa de demolición" es obligatorio cuando "Demolido" está marcado.');
    }

    if (errores.length > 0) {
      Swal.fire({
        icon: 'error',
        title: 'Campos obligatorios faltantes',
        text: errores.join(' '),
        confirmButtonColor: '#dc3545'
      });
      return false;
    }
    return true;
  } catch (e) {
    return true;
  }
};

export const validarRecipientesCompleto = (acta) => {
  try {
    let recipientes = acta?.recipientes_detalle;
    if (!recipientes) return true;
    if (typeof recipientes === 'string') {
      try { recipientes = JSON.parse(recipientes || '[]'); } catch (_) { recipientes = []; }
    }
    if (!Array.isArray(recipientes) || recipientes.length === 0) return true;

    const requiredFields = ['descripcion','largo','ancho','alto','cantidad'];
    const isFilled = (v) => v !== '' && v !== null && v !== undefined;

    for (let i = 0; i < recipientes.length; i++) {
      const row = recipientes[i] || {};
      const anyValue = requiredFields.some((k) => isFilled(row[k])) || isFilled(row.m3);
      if (anyValue) {
        const missingRequired = requiredFields.filter((k) => !isFilled(row[k]));
        const missingM3 = !isFilled(row.m3);
        if (missingRequired.length > 0 || missingM3) {
          Swal.fire({
            icon: 'error',
            title: 'No se puede finalizar',
            text: `En Detalles de Recipientes, fila ${i + 1}: complete todos los campos (Recipiente, Largo, Ancho, Alto, Cantidad y m³) o deje la fila completamente vacía.`,
            confirmButtonColor: '#dc3545'
          });
          return false;
        }
      }
    }
    return true;
  } catch (e) {
    return true;
  }
};

// Helpers para validar por tipo de orden
const hasURData = (acta) => {
  const ur = parseArray(acta?.unidades_residenciales_detalle);
  return hasAnyDataInRows(ur);
};

const hasUNRData = (acta) => {
  const unr = parseArray(acta?.unidades_no_residenciales_detalle);
  return hasAnyDataInRows(unr);
};

const hasCaracteristicasPredioData = (acta) => {
  const fields = [
    'abandonado', 'suscriptor_inactivo', 'demolido', 'en_construccion',
    'en_remodelacion', 'inexistente', 'inhabitable_o_en_ruinas', 'lote',
    'minimas_condiciones', 'programa_del_icbf', 'descuento_puerta_a_puerta',
    'se_cierra_pqr_como_no_atendido', 'areas_comunes', 'predio_que_se_observa_vacio',
    'predio_se_observa_ocupado', 'cuenta_suscriptor_activo'
  ];
  return fields.some(f => !!acta?.[f]);
};

const hasTiposResiduosRecipientes = (acta) => {
  return !!(
    acta?.escombros || acta?.especiales || acta?.para_relleno || acta?.para_escombrera ||
    acta?.enlonado || acta?.a_granel || (acta?.tipo_recoleccion && String(acta.tipo_recoleccion).trim() !== '')
  );
};

const hasDetallesRecipientes = (acta) => {
  const rec = parseArray(acta?.recipientes_detalle);
  return hasAnyDataInRows(rec);
};

export const validarActaTieneDatos = (acta) => {
  try {
    // Si el acta está cancelada, no aplican las validaciones de contenido
    if (acta?.cancelado) return true;

    const hasUR = hasURData(acta);
    const hasUNR = hasUNRData(acta);
    const hasCaracteristicas = hasCaracteristicasPredioData(acta);
    const hasTiposResiduos = hasTiposResiduosRecipientes(acta);
    const hasRecipientes = hasDetallesRecipientes(acta);
    const hasActualizacion = !!(
      acta?.actualizacion_nombre_suscriptor || acta?.actualizacion_direccion || acta?.actualizacion_telefono
    );

    if (hasUR || hasUNR || hasCaracteristicas || hasTiposResiduos || hasRecipientes || hasActualizacion) {
      return true;
    }

    Swal.fire({
      icon: 'warning',
      title: 'Acta sin datos',
      text: 'El acta no contiene datos en Unidades Residenciales, Características del predio, Tipos de residuos o Actualización de datos. Complete al menos uno de estos antes de finalizar, o marque el acta como cancelada.',
      confirmButtonColor: '#dc3545'
    });

    return false;
  } catch (e) {
    return true;
  }
};

export const validarPorTipoOrden = (acta, caso) => {
  try {
    const tipoOrden = caso?.tipo_orden;
    if (tipoOrden === 'PQRS') {
      if (!(hasURData(acta) || hasUNRData(acta) || hasCaracteristicasPredioData(acta))) {
        Swal.fire({
          icon: 'warning',
          title: 'Faltan datos requeridos (PQRS)',
          text: 'Para tipo de orden PQRS, diligencie al menos uno: Unidades Residenciales (UR), Unidades No Residenciales (UNR) o Características del Predio.',
          confirmButtonColor: '#dc3545'
        });
        return false;
      }
    } else if (tipoOrden === 'RCD') {
      if (acta?.cancelado) return true;
      if (!(hasTiposResiduosRecipientes(acta) && hasDetallesRecipientes(acta))) {
        Swal.fire({
          icon: 'warning',
          title: 'Faltan datos requeridos (RCD)',
          text: 'Para tipo de orden RCD, diligencie ambos: Tipos de Residuos y Recipientes, y Detalles de Recipientes. Si el acta está cancelada, esta validación no aplica.',
          confirmButtonColor: '#dc3545'
        });
        return false;
      }
    }
    return true;
  } catch (e) {
    return true;
  }
};

export default {
  validarCedulaRepresentanteEscombros,
  validarUNRCompleto,
  validarURCompleto,
  validarCamposCondicionales,
  validarRecipientesCompleto,
  validarPorTipoOrden
  , validarActaTieneDatos
};
